// Single source of truth for the weighted-set volume multiplier.
//
// `loadedSides` is the per-set bilateral/unilateral marker:
//   • absent / 1 → bilateral → the entered `weight` is the TOTAL load
//     moved (a 100 kg barbell is 100 kg of work). Multiplier 1.
//   • 2 → unilateral → one side is logged but both are performed, so
//     volume counts both sides. Multiplier 2.
//
// The default is 1. Historically the read-default was 2, which doubled
// every weighted set's volume — and since no set ever persisted the
// field, that doubling hit the entire dataset at read time. Bilateral
// is the overwhelmingly common case (barbells, machines, cables), so 1
// is the correct absent-value default; unilateral exercises seed and
// persist an explicit 2 at log time (driven by Exercise.unilateral).
export const setLoadMultiplier = (loadedSides: 1 | 2 | undefined): number =>
  loadedSides ?? 1;

// Catalog → laterality seed, the OTHER half of the single source: which
// loadedSides a set STARTS with when nobody chose. Unilateral catalog
// rows seed ×2 (one side/implement logged, both counted), everything
// else — including cardio, whatever its flags claim — seeds ×1. Shared
// because the rule is enforced twice on purpose: the mobile logger seeds
// entries with it (add / plan-start / swap, via deriveEntryKind's
// re-export) and the API stamps it on any weighted/bodyweight payload
// that arrives WITHOUT an explicit value (IB-8 Slice 2,
// setFactory.buildLogSet) — two copies of the predicate would be the
// exact divergence this family of bugs keeps growing from. The loose
// param shape lets callers pass a possibly-missing catalog row.
export const catalogLoadedSides = (ex: {
  movementPattern?: string | null;
  unilateral?: boolean | null;
}): 1 | 2 =>
  ex.movementPattern !== "cardio" && ex.unilateral === true ? 2 : 1;

// Volume contribution of a single weighted set: weight × reps × sides.
// Pure arithmetic, so the formula lives in exactly one place.
//
// It takes no view on `fail`, and callers must not either: under the AP-0p
// model (schemas/workout.ts SetFail) `reps` already holds the clean reps
// completed, so a failed set contributes volume like any other. Excluding
// fails here is the pre-2026-07-06 rule that was dropped precisely because it
// lost real reps. progress (×2) and bodymap count them explicitly — no `fail`
// filter on the sets they walk; dropsetTotals never sees one, since
// DropsetRound carries no such field.
//
// Zero weight or reps needs no filtering either: both are min(0) in the
// schema, so the product is simply 0. bodymap and dropsetTotals do skip such
// sets early, but to answer a different question than this arithmetic —
// bodymap whether the ENTRY contributed anything, dropsetTotals whether a
// ROUND contributed to its set's subtotal.
export const weightedVolumeKg = (
  weightKg: number,
  reps: number,
  loadedSides?: 1 | 2,
): number => weightKg * reps * setLoadMultiplier(loadedSides);
